/**
 * Full raw-event display for a trace.
 *
 * Renders the verbatim, UNREDACTED SQS message body captured on the trace
 * header (`rawEventBody`) — the faithful "exactly what we received" audit copy,
 * distinct from the redacted/capped per-step inputs shown in the timeline.
 *
 * Pretty-prints as JSON when the body parses; falls back to raw monospace text
 * for malformed messages (which are captured too). Verbatim copy-to-clipboard
 * copies the ORIGINAL string, never the reformatted JSON.
 */
import { ContentCopy, DoneAll, Replay } from "@mui/icons-material"
import { Box, Button, Chip, Stack, Typography } from "@mui/material"
import { FC, useCallback, useMemo, useState } from "react"
import { JsonView } from "react-json-view-lite"

import "react-json-view-lite/dist/index.css"

import { fmtBytes } from "./formatters"
import type { TraceItem } from "./types"

type ParsedBody =
  | { kind: "json"; value: object }
  | { kind: "text"; value: string }

const parseBody = (body: string): ParsedBody => {
  try {
    const value = JSON.parse(body) as unknown
    // Only treat objects/arrays as structured; primitives render fine as text.
    if (value !== null && typeof value === "object") {
      return { kind: "json", value: value as object }
    }
  } catch {
    /* fall through to raw text */
  }
  return { kind: "text", value: body }
}

const byteLength = (s: string): number => new TextEncoder().encode(s).length

export const Sstv2RawEventPanel: FC<{ trace: TraceItem }> = ({ trace }) => {
  const [copied, setCopied] = useState(false)
  const body = trace.rawEventBody

  const parsed = useMemo(
    () => (body != null ? parseBody(body) : undefined),
    [body],
  )

  const displaySize = useMemo(() => {
    if (body == null) return undefined
    return trace.rawEventBodyOriginalSize ?? byteLength(body)
  }, [body, trace.rawEventBodyOriginalSize])

  const handleCopy = useCallback(() => {
    if (body == null) return
    void navigator.clipboard?.writeText(body).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }, [body])

  if (body == null) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Full event received
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No raw event body was captured for this trace (predates raw-event
          capture, or was written by a non-SQS source).
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1, flexWrap: "wrap" }}
      >
        <Typography variant="subtitle2">Full event received</Typography>
        <Chip
          size="small"
          variant="outlined"
          color="info"
          label="verbatim · unredacted"
        />
        {trace.approximateReceiveCount != null &&
          (trace.approximateReceiveCount > 1 ? (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              icon={<Replay fontSize="small" />}
              label={`redelivered · receipt #${trace.approximateReceiveCount}`}
            />
          ) : (
            <Chip size="small" variant="outlined" label="first delivery" />
          ))}
        {displaySize != null && (
          <Chip
            size="small"
            variant="outlined"
            label={fmtBytes(displaySize)}
          />
        )}
        {trace.rawEventBodyTruncated && (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label={`truncated · stored a ${fmtBytes(
              trace.rawEventBodyOriginalSize,
            )} original`}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={copied ? <DoneAll fontSize="small" /> : <ContentCopy fontSize="small" />}
          onClick={handleCopy}
        >
          {copied ? "Copied" : "Copy raw"}
        </Button>
      </Stack>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "rgba(35, 66, 133, 0.03)",
          p: 1.5,
          maxHeight: 480,
          overflow: "auto",
          fontSize: 12,
        }}
      >
        {parsed?.kind === "json" ? (
          <JsonView data={parsed.value} />
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: 12,
            }}
          >
            {parsed?.value}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default Sstv2RawEventPanel
